import React, { memo, useCallback, useEffect, useState } from 'react';
import { Col } from 'react-bootstrap';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';

import { SkeletonLoading } from '@saleshandy/design-system';
import { useSelector } from 'react-redux';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import * as Icons from '../../../../shared/svg';
import { RootState } from '../../../../store/root-reducer';
import {
  FilterComponentType,
  GetLeadsV2CountResponse,
  GetCompaniesV2CountResponse,
  LeadFinderPrimaryTab,
  LeadFinderSecondaryTab,
  SavedTabFilters,
} from '../../types';
import LeadsFilterBulkSearch from '../filter/leads-filter-bulk-search';
import { BulkFilters } from '../filter/leads-filter-bulk-search/type';
import { useLeadFilter } from '../hooks/useLeadFilter';
import { useCompanyFilter } from '../hooks/useCompanyFilter';
import { leadFinderViewOnly } from '../../../../shared/utils/user-details';
import V2LeadFilter from '../filter/v2-lead-filter';
import V2CompanyFilter from '../filter/v2-company-filter';
import V2SavedTab, { SavedListData } from '../filter/v2-saved-tab';

interface V2LeadSidebarProps {
  isSidebarExpanded: boolean;
  setIsSidebarExpanded: (value: boolean) => void;
  handleRecentSearch: any;
  handleCompanyRecentSearch?: () => void;
  handleGeneratePayload: any;
  setSelectedLeads: (value?: any) => void;
  showPhoneNumberNudge: boolean;
  setShowPhoneNumberNudge: React.Dispatch<React.SetStateAction<boolean>>;
  setIgnorePhoneNumberNudge: React.Dispatch<React.SetStateAction<boolean>>;
  searchLeads: (payload?: any, performSearch?: boolean) => void;
  isLoading: boolean;
  isLoadingCounts: boolean;
  isLoadingSavedCounts: boolean;
  keywordInputRef: React.RefObject<HTMLInputElement>;
  activeSubTab: LeadFinderSecondaryTab;
  onFilterTabChange: (tab: LeadFinderSecondaryTab) => void;
  leadTags: any[];
  isLeadTagsLoading: boolean;
  savedFilters: SavedTabFilters;
  setSavedFilters: React.Dispatch<React.SetStateAction<SavedTabFilters>>;
  countsData: GetLeadsV2CountResponse['payload'] | null;
  countsError: boolean;
  handleRouteChange: () => void;
  // Company tab props
  activeLeadType?: LeadFinderPrimaryTab;
  searchCompanies?: (payload?: any, performSearch?: boolean) => void;
  setSelectedCompanies?: (value?: any) => void;
  companyCountsData?: GetCompaniesV2CountResponse['payload'] | null;
  companyCountsError?: boolean;
  isCompanyLoading?: boolean;
  isLoadingCompanyCounts?: boolean;
  isLoadingCompanySavedCounts?: boolean;
  handleCompanyGeneratePayload?: any;
  companyListsData?: SavedListData[];
  peopleTabCompanyListsData?: SavedListData[];
  isLoadingCompanyLists?: boolean;
  isLoadingPeopleLists?: boolean;
  peopleListsData?: SavedListData[];
  onSaveList?: (
    listId: number | null,
    listName: string,
    isShared: boolean,
    teamIds: number[],
    shouldEnrich?: boolean,
  ) => Promise<void>;
  onCreateListOnly?: (
    listName: string,
    isShared: boolean,
    teamIds: number[],
  ) => Promise<void>;
  isUpdateListLoading?: boolean;
  onExportList?: (listId: number, listName: string) => void | Promise<void>;
  onDeleteList?: (listId: number) => Promise<void>;
  isDeleteListLoading?: boolean;
  teamsList?: any[];
  getTeamsListRequestStatus?: any;
  sendGetTeamsListRequest?: VoidFunction;
  hasPaymentPendingBanner?: boolean;
  hasDemoAccountBanner?: boolean;
  signalsListData?: Array<{ key: string; value: string }>;
  newsListData?: Array<{ key: string; value: string }>;
  loginUserEmail?: string;
  triggerSearchWithFilters?: (filters: URLSearchParams) => void;
  onClearAll?: () => void;
  /** Ref updated by sidebar with current tab's bulk-search flag; parent uses it e.g. on tab change to avoid retaining filters in URL */
  skipUrlPushForBulkSearchRef?: React.MutableRefObject<boolean>;
  tabSpecificFiltersRef?: React.MutableRefObject<{
    people: string;
    company: string;
  }>;
  tabSpecificBulkModeRef?: React.MutableRefObject<{
    people: boolean;
    company: boolean;
  }>;
  syncCompanyTabSpecificFiltersRef?: (filters: URLSearchParams) => void;
  syncPeopleTabSpecificFiltersRef?: (filters: URLSearchParams) => void;
  companyFilterRestoreRef?: React.MutableRefObject<
    ((params: URLSearchParams) => void) | null
  >;
  peopleFilterRestoreRef?: React.MutableRefObject<
    ((params: URLSearchParams) => void) | null
  >;
}

const V2LeadSidebar: React.FC<V2LeadSidebarProps> = (props) => {
  const history = useHistory();
  const location = useLocation();

  const {
    isSidebarExpanded,
    setIsSidebarExpanded,
    handleRecentSearch,
    handleCompanyRecentSearch,
    handleGeneratePayload,
    setSelectedLeads,
    showPhoneNumberNudge,
    setShowPhoneNumberNudge,
    setIgnorePhoneNumberNudge,
    searchLeads,
    isLoading,
    isLoadingCounts,
    isLoadingSavedCounts,
    keywordInputRef,
    activeSubTab,
    onFilterTabChange,
    leadTags,
    isLeadTagsLoading,
    savedFilters,
    setSavedFilters,
    countsData,
    countsError,
    handleRouteChange,
    // Company tab props
    activeLeadType = LeadFinderPrimaryTab.PEOPLE,
    searchCompanies,
    setSelectedCompanies,
    companyCountsData,
    companyCountsError,
    isCompanyLoading,
    isLoadingCompanyCounts,
    isLoadingCompanySavedCounts,
    handleCompanyGeneratePayload,
    companyListsData = [],
    peopleTabCompanyListsData = [],
    isLoadingCompanyLists = false,
    isLoadingPeopleLists = false,
    peopleListsData,
    onSaveList,
    onCreateListOnly,
    isUpdateListLoading = false,
    onExportList,
    onDeleteList,
    teamsList = [],
    getTeamsListRequestStatus,
    sendGetTeamsListRequest = () => {},
    hasPaymentPendingBanner = false,
    hasDemoAccountBanner = false,
    signalsListData = [],
    newsListData = [],
    loginUserEmail,
    triggerSearchWithFilters,
    onClearAll,
    skipUrlPushForBulkSearchRef,
    tabSpecificFiltersRef,
    tabSpecificBulkModeRef,
    syncCompanyTabSpecificFiltersRef,
    syncPeopleTabSpecificFiltersRef,
    companyFilterRestoreRef,
    peopleFilterRestoreRef,
  } = props;

  const isCompanyTab = activeLeadType === LeadFinderPrimaryTab.COMPANY;

  const isAiSearchInProgress = useSelector(
    (state: RootState) => state?.leadFinderV2?.isAiSearchInProgress,
  );

  // Use company lists from the appropriate source based on active tab
  const effectiveCompanyListsData = isCompanyTab
    ? companyListsData
    : peopleTabCompanyListsData;

  const [showBulkSearch, setShowBulkSearch] = useState(false);
  const [
    bulkSearchFocusFilterKey,
    setBulkSearchFocusFilterKey,
  ] = useState<string>();
  const [bulkFilterSyncKey, setBulkFilterSyncKey] = useState<
    Record<string, boolean>
  >({});

  const {
    handleDeleteFilter,
    handleGetAllKeysByFilterFromQuery,
    handleIndividualTag,
    handleFoundValueExceptFromList,
    handleQuery,
    handleBulkQuery,
    applyPendingFilters,
    applyExternalBulkFilters,
    hasPendingFilters,
    syncPendingFiltersWithUrl,
    clearPendingFilters,
    pendingFilters,
    companyIdNameMap,
    setCompanyIdNameMap,
    skipUrlPushForBulkSearch: peopleSkipUrlPushForBulkSearch,
    setSkipUrlPushForBulkSearch: setPeopleSkipUrlPushForBulkSearch,
    restoreFilterUI: restorePeopleFilterUI,
    isLimitFilterInvalid,
    handleLimitValidationChange,
  } = useLeadFilter(
    activeSubTab,
    searchLeads,
    peopleListsData,
    effectiveCompanyListsData,
    activeLeadType,
    loginUserEmail,
    triggerSearchWithFilters,
    {
      syncTabSpecificPeopleFiltersRef: syncPeopleTabSpecificFiltersRef,
    },
  );

  // Company filter hook
  const safeSearchCompanies = isCompanyTab
    ? searchCompanies || (() => {})
    : () => {};
  const {
    handleDeleteFilter: handleCompanyDeleteFilter,
    handleGetAllKeysByFilterFromQuery: handleCompanyGetAllKeysByFilterFromQuery,
    handleIndividualTag: handleCompanyIndividualTag,
    handleQuery: handleCompanyQuery,
    applyPendingFilters: applyCompanyPendingFilters,
    applyExternalBulkFilters: applyCompanyExternalBulkFilters,
    hasPendingFilters: hasCompanyPendingFilters,
    clearPendingFilters: clearCompanyPendingFilters,
    pendingFilters: companyPendingFilters,
    companyIdNameMap: companyCompanyIdNameMap,
    setCompanyIdNameMap: setCompanyCompanyIdNameMap,
    isPartialFilterApplied,
    skipUrlPushForBulkSearch: companySkipUrlPushForBulkSearch,
    setSkipUrlPushForBulkSearch: setCompanySkipUrlPushForBulkSearch,
    restoreFilterUI: restoreCompanyFilterUI,
  } = useCompanyFilter(
    activeSubTab,
    safeSearchCompanies,
    companyListsData,
    signalsListData,
    newsListData,
    activeLeadType,
    loginUserEmail,
    triggerSearchWithFilters,
    {
      initialFilters: tabSpecificFiltersRef?.current?.company,
      initialSkipUrlPushForBulkSearch: !!tabSpecificBulkModeRef?.current
        ?.company,
      syncTabSpecificCompanyFiltersRef: syncCompanyTabSpecificFiltersRef,
      tabSpecificFiltersRef,
    },
  );

  useEffect(() => {
    if (skipUrlPushForBulkSearchRef) {
      skipUrlPushForBulkSearchRef.current = isCompanyTab
        ? companySkipUrlPushForBulkSearch
        : peopleSkipUrlPushForBulkSearch;
    }
    if (tabSpecificBulkModeRef) {
      if (isCompanyTab) {
        tabSpecificBulkModeRef.current.company = companySkipUrlPushForBulkSearch;
      } else {
        tabSpecificBulkModeRef.current.people = peopleSkipUrlPushForBulkSearch;
      }
    }
    if (companyFilterRestoreRef) {
      companyFilterRestoreRef.current = restoreCompanyFilterUI;
    }
    if (peopleFilterRestoreRef) {
      peopleFilterRestoreRef.current = restorePeopleFilterUI;
    }
  }, [
    skipUrlPushForBulkSearchRef,
    isCompanyTab,
    companySkipUrlPushForBulkSearch,
    peopleSkipUrlPushForBulkSearch,
    tabSpecificBulkModeRef,
    companyFilterRestoreRef,
    restoreCompanyFilterUI,
    peopleFilterRestoreRef,
    restorePeopleFilterUI,
  ]);

  const onSidebarExpansionChange = useCallback(() => {
    setIsSidebarExpanded(!isSidebarExpanded);
  }, [isSidebarExpanded, setIsSidebarExpanded]);

  const onBulkSearchShow = useCallback(
    (filterKey: string) => {
      setBulkSearchFocusFilterKey(filterKey);
      setShowBulkSearch(true);
    },
    [setBulkSearchFocusFilterKey, setShowBulkSearch],
  );
  
  const onBulkSearch = useCallback(
    (bulkFilters: BulkFilters) => {
      setShowBulkSearch(false);

      if (location?.search) {
        if (skipUrlPushForBulkSearchRef) {
          skipUrlPushForBulkSearchRef.current = true;
        }
        history.replace({ search: '' });
      }

      const bulkQuery = [];
      const newBulkFilterSyncKeys: Record<string, boolean> = {};
      Object.keys(bulkFilters).forEach((bulkFilterKey) => {
        const bulkFilter = bulkFilters[bulkFilterKey];
        const effectiveFilterKey =
          bulkFilterKey === 'company_name' ? 'company_ids' : bulkFilterKey;
        bulkQuery.push({
          filterKey: effectiveFilterKey,
          filterValue: {
            includedKeys: bulkFilter.values,
          },
          filterType: FilterComponentType.MULTILEVEL_CHECKBOX,
          showExactMatch: bulkFilter.showExactMatch,
        });
        if (bulkFilter?.values?.length) {
          newBulkFilterSyncKeys[bulkFilterKey] = true;
        }
      });
      setPeopleSkipUrlPushForBulkSearch(true);
      const alreadyInBulkMode =
        peopleSkipUrlPushForBulkSearch && !location?.search;
      // Apply bulk filters without pushing to URL.
      handleBulkQuery(
        bulkQuery,
        (updatedFilters) => {
          applyPendingFilters(updatedFilters, { skipUrlPush: true });
        },
        alreadyInBulkMode
          ? undefined
          : { baseFilters: new URLSearchParams(location?.search || '') },
      );

      setTimeout(() => setBulkFilterSyncKey(newBulkFilterSyncKeys), 0);
    },
    [
      handleBulkQuery,
      applyPendingFilters,
      peopleSkipUrlPushForBulkSearch,
      location?.search,
      skipUrlPushForBulkSearchRef,
    ],
  );

  const sideNavClassName = classNames([
    'v2-leads-sidebar',
    'v2-leadsSideNav',
    {
      'v2-sidenav--collapse': !isSidebarExpanded,
    },
  ]);

  const limitPerCompanyFilterPresent = pendingFilters?.get('limit');

  const formatCount = (count?: number): string => {
    if (count === undefined || count === null) {
      return '';
    }
    if (limitPerCompanyFilterPresent) {
      const limitValue = Number(limitPerCompanyFilterPresent);
      if (!Number.isNaN(limitValue) && limitValue > 0) {
        if (limitValue * 10000 === count) {
          const formatted = (count / 1000).toFixed(1);
          return `${parseFloat(formatted)}K+`;
        }
      }
    }
    if (count >= 1000000) {
      const formatted = (count / 1000000).toFixed(1);
      return `${parseFloat(formatted)}M`;
    }
    if (count === 10000) {
      return '10K+';
    }
    if (count >= 1000) {
      const formatted = (count / 1000).toFixed(1);
      return `${parseFloat(formatted)}K`;
    }
    return count.toLocaleString('en-US');
  };

  // Determine which counts to use based on active tab
  const currentCountsData = isCompanyTab ? companyCountsData : countsData;
  const currentCountsError = isCompanyTab ? companyCountsError : countsError;
  const currentIsLoadingCounts = isCompanyTab
    ? isLoadingCompanyCounts
    : isLoadingCounts;

  const isCountApiLoading =
    currentIsLoadingCounts ||
    (activeSubTab === LeadFinderSecondaryTab.SAVED &&
      (isCompanyTab ? isLoadingCompanySavedCounts : isLoadingSavedCounts));

  const FILTER_TABS: Array<{
    key: LeadFinderSecondaryTab;
    label: string;
    count: string;
  }> = [
    {
      key: LeadFinderSecondaryTab.TOTAL,
      label: 'Total',
      count: currentCountsError
        ? ''
        : formatCount(currentCountsData?.totalCount),
    },
    {
      key: LeadFinderSecondaryTab.NEW,
      label: 'New',
      count: currentCountsError
        ? ''
        : formatCount(currentCountsData?.netNewCount),
    },
    {
      key: LeadFinderSecondaryTab.SAVED,
      label: 'Saved',
      count: (() => {
        if (currentCountsError) return '';
        const savedCount = isCompanyTab
          ? companyCountsData?.savedCount ??
            (companyCountsData as any)?.revealedCount
          : countsData?.revealedCount;
        return formatCount(savedCount);
      })(),
    },
  ];

  // Saved List data structure
  // const savedListsData: SavedListData[] = [
  //   {
  //     id: 12365,
  //     label: 'B2B Companies',
  //     visibility: LeadFinderListVisibility.Public,
  //     count: 10,
  //     owner: {
  //       firstName: 'John',
  //       lastName: 'Doe',
  //     },
  //   },
  //   {
  //     id: 12358,
  //     label: 'Mid Cap Companies',
  //     visibility: LeadFinderListVisibility.Private,
  //     count: 16,
  //     owner: {
  //       firstName: 'Meet',
  //       lastName: 'Soni',
  //     },
  //   },
  //   {
  //     id: 12366,
  //     label: 'B2B Start-ups',
  //     visibility: LeadFinderListVisibility.Public,
  //     count: 25,
  //     owner: {
  //       firstName: 'D',
  //       lastName: 'T',
  //     },
  //   },
  //   {
  //     id: 12367,
  //     label: 'New D2C Start-ups',
  //     visibility: LeadFinderListVisibility.Private,
  //     count: 8,
  //     owner: {
  //       firstName: 'N',
  //       lastName: 'D',
  //     },
  //   },
  //   {
  //     id: 12368,
  //     label: 'Large Union Companies',
  //     visibility: LeadFinderListVisibility.Private,
  //     count: 12,
  //     owner: {
  //       firstName: 'L',
  //       lastName: 'U',
  //     },
  //   },
  // ];

  // // Company Saved List data structure (for Add to List dropdown)
  // const companySavedListsData = [
  //   {
  //     id: 1236,
  //     label: 'B2B Companies',
  //     visibility: LeadFinderListVisibility.Public,
  //     owner: {
  //       firstName: 'John',
  //       lastName: 'Doe',
  //     },
  //   },
  //   {
  //     id: 12896,
  //     label: 'Mid Cap',
  //     visibility: LeadFinderListVisibility.Private,
  //     owner: {
  //       firstName: 'Meet',
  //       lastName: 'Soni',
  //     },
  //   },
  //   {
  //     id: 1237,
  //     label: 'Teams filter',
  //     visibility: LeadFinderListVisibility.Team,
  //     owner: {
  //       firstName: 'John',
  //       lastName: 'Doe',
  //     },
  //   },
  // ];

  // Saved List action handlers
  const handleEditList = (listId: number, listName: string) => {
    console.log('Edit list:', listId, listName);
    // Handle edit action
  };

  const handleExportList = (listId: number, listName: string) => {
    onExportList?.(listId, listName);
  };

  const handleDeleteList = async (listId: number) => {
    if (onDeleteList) {
      await onDeleteList(listId);
    }
  };

  const handleUpdateList = async (
    listId: number | null,
    listName: string,
    isShared: boolean,
    teamIds: number[],
  ) => {
    if (onSaveList) {
      await onSaveList(listId, listName, isShared, teamIds, true);
    }
  };

  const renderFilters = () => {
    if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
      const listsDataToUse = isCompanyTab
        ? companyListsData || []
        : peopleListsData || [];

      const isSavedResultsLoading = isCompanyTab
        ? !!isCompanyLoading
        : !!isLoading;
      return (
        <V2SavedTab
          leadTags={leadTags}
          isLeadTagsLoading={isLeadTagsLoading}
          isSavedTabLoading={isSavedResultsLoading}
          activeSubTab={activeSubTab}
          savedFilters={savedFilters}
          setSavedFilters={setSavedFilters}
          savedListsData={listsDataToUse}
          allListsData={[
            ...(companyListsData || []),
            ...(peopleListsData || []),
          ]}
          onEditList={handleEditList}
          onExportList={handleExportList}
          onDeleteList={handleDeleteList}
          onCreateList={onCreateListOnly}
          onUpdateList={handleUpdateList}
          teamsList={teamsList}
          getTeamsListRequestStatus={getTeamsListRequestStatus}
          sendGetTeamsListRequest={sendGetTeamsListRequest}
          isUpdateListLoading={isUpdateListLoading}
          activeLeadType={activeLeadType}
        />
      );
    }

    if (isCompanyTab) {
      return (
        <V2CompanyFilter
          handleRecentSearch={handleCompanyRecentSearch}
          handleGeneratePayload={
            handleCompanyGeneratePayload || handleGeneratePayload
          }
          setSelectedCompanies={setSelectedCompanies || (() => {})}
          handleDeleteFilter={handleCompanyDeleteFilter}
          handleGetAllKeysByFilterFromQuery={
            handleCompanyGetAllKeysByFilterFromQuery
          }
          handleIndividualTag={handleCompanyIndividualTag}
          handleQuery={handleCompanyQuery}
          searchCompanies={searchCompanies || (() => {})}
          isLoading={isCompanyLoading || false}
          keywordInputRef={keywordInputRef}
          activeSubTab={activeSubTab}
          applyPendingFilters={applyCompanyPendingFilters}
          applyExternalBulkFilters={applyCompanyExternalBulkFilters}
          hasPendingFilters={hasCompanyPendingFilters}
          isPartialFilterApplied={isPartialFilterApplied}
          clearPendingFilters={clearCompanyPendingFilters}
          pendingFilters={companyPendingFilters}
          companyListsData={effectiveCompanyListsData}
          isLoadingLists={isLoadingCompanyLists}
          signalsListData={signalsListData}
          newsListData={newsListData}
          companyIdNameMap={companyCompanyIdNameMap}
          setCompanyIdNameMap={setCompanyCompanyIdNameMap}
          setSkipUrlPushForBulkSearch={setCompanySkipUrlPushForBulkSearch}
          skipUrlPushForBulkSearchRef={skipUrlPushForBulkSearchRef}
          onClearAll={onClearAll}
          skipUrlPushForBulkSearch={companySkipUrlPushForBulkSearch}
          isLoadingCounts={isCountApiLoading}
        />
      );
    }

    return (
      <V2LeadFilter
        {...{
          handleRecentSearch,
          handleGeneratePayload,
          setSelectedLeads,
          onBulkSearchShow,
          handleDeleteFilter,
          handleGetAllKeysByFilterFromQuery,
          handleIndividualTag,
          handleFoundValueExceptFromList,
          handleQuery,
          bulkFilterSyncKey,
          showPhoneNumberNudge,
          setShowPhoneNumberNudge,
          setIgnorePhoneNumberNudge,
          searchLeads,
          isLoading,
          keywordInputRef,
          activeSubTab,
          applyPendingFilters,
          hasPendingFilters,
          syncPendingFiltersWithUrl,
          clearPendingFilters,
          pendingFilters,
          handleRouteChange,
          onClearAll,
          skipUrlPushForBulkSearch: peopleSkipUrlPushForBulkSearch,
          peopleListsData: peopleListsData || [],
          companyListsData: effectiveCompanyListsData || [],
          isLoadingCompanyLists,
          isLoadingPeopleLists,
          companyIdNameMap,
          setCompanyIdNameMap,
          applyExternalBulkFilters,
          isLimitFilterInvalid,
          handleLimitValidationChange,
          isLoadingCounts: isCountApiLoading,
        }}
      />
    );
  };

  const handleFilterTabClick = (tab: LeadFinderSecondaryTab) => {
    if (tab === activeSubTab) {
      return;
    }
    onFilterTabChange(tab);
  };

  return (
    <Col
      as="aside"
      lg={3}
      className={classNames('v2-leads-navigation', sideNavClassName, {
        'v2-leads-navigation--with-payment-banner': hasPaymentPendingBanner,
        'v2-leads-navigation--with-demo-banner': hasDemoAccountBanner,
      })}
      aria-expanded={isSidebarExpanded}
    >
      <div className="v2-lead-sidebar-content">
        <div
          className="v2-expand-btn"
          {...accessibleOnClick(onSidebarExpansionChange)}
        >
          {isSidebarExpanded ? (
            <Icons.ChevronLeft width={17} height={17} className="gray-txt-15" />
          ) : (
            <Icons.ChevronRight
              width={17}
              height={17}
              className="gray-txt-15"
            />
          )}
        </div>
        {isSidebarExpanded && (
          <>
            <div className="v2-lead-sidebar-tabs-container">
              <div className="v2-lead-sidebar-tabs">
                {FILTER_TABS.map((tab) => {
                  const isTotalOrNew =
                    tab.key === LeadFinderSecondaryTab.TOTAL ||
                    tab.key === LeadFinderSecondaryTab.NEW;
                  const isSaved = tab.key === LeadFinderSecondaryTab.SAVED;
                  const isNewTab = tab.key === LeadFinderSecondaryTab.NEW;
                  const isCountLoading =
                    (isTotalOrNew && (currentIsLoadingCounts || false)) ||
                    // Default view derives netNew from the saved count; hold the
                    // New skeleton until that resolves (netNew undefined).
                    (isNewTab &&
                      currentCountsData?.netNewCount === undefined &&
                      !currentCountsError) ||
                    (!isCompanyTab && isAiSearchInProgress) ||
                    (isSaved &&
                      (isCompanyTab
                        ? isLoadingCompanySavedCounts || false
                        : isLoadingSavedCounts || false));

                  const renderCount = () => {
                    if (isCountLoading) {
                      return (
                        <div className="v2-lead-sidebar-tabs__tab-count-loader">
                          <SkeletonLoading
                            height={20}
                            width={46}
                            borderRadius={20}
                          />
                        </div>
                      );
                    }
                    if (tab.count) {
                      return (
                        <span className="v2-lead-sidebar-tabs__tab-count">
                          {tab.count}
                        </span>
                      );
                    }
                    return null;
                  };

                  return (
                    <button
                      type="button"
                      key={tab.key}
                      className={classNames('v2-lead-sidebar-tabs__tab', {
                        'is-active': activeSubTab === tab.key,
                        'is-disabled':
                          tab.key === LeadFinderSecondaryTab.SAVED &&
                          leadFinderViewOnly() === true,
                      })}
                      onClick={() => handleFilterTabClick(tab.key)}
                      disabled={
                        tab.key === LeadFinderSecondaryTab.SAVED &&
                        leadFinderViewOnly() === true
                      }
                    >
                      <span className="v2-lead-sidebar-tabs__tab-label">
                        {tab.label}
                      </span>
                      {renderCount()}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="v2-lead-filter-wrapper">{renderFilters()}</div>
          </>
        )}

        <LeadsFilterBulkSearch
          show={showBulkSearch}
          onClose={() => setShowBulkSearch(false)}
          onSearch={onBulkSearch}
          focusKey={bulkSearchFocusFilterKey}
          pendingFilters={pendingFilters}
        />
      </div>
    </Col>
  );
};

export default memo(V2LeadSidebar);
